/**
 * Sample Campaign — Phase 3 (gates) + Phase 4 (submit pipeline)
 *
 * Page-load gates:
 *   1. URL ?src= param  — missing → "unavailable"
 *   2. IP geolocation   — non-UK → "uk-only" state
 *   3. Otherwise        → "quiz" state
 * Geo failure / timeout → fail open to quiz (UK-only shipping at checkout
 * is the hard block; do not punish UK users for a 3rd-party hiccup).
 *
 * Customiser bypass: when data-design-mode="true" (Liquid request.design_mode),
 * both the ?src= and geo gates are skipped so editors can always preview the
 * quiz state.
 *
 * Submit pipeline (Phase 4):
 *   1. Submit hidden Klaviyo Sign-up Form (mirrors assets/klaviyo-signup.js)
 *      → extract unique single-use code from success view DOM.
 *   2. Klaviyo profile update with email + quiz_q1/q2/q3 + campaign_src via the
 *      client-side profiles API with keepalive (survives the checkout redirect;
 *      a fire-and-forget _learnq push often lost the race and dropped answers).
 *   3. POST /cart/add.js with all sample variant IDs.
 *   4. POST /cart/update.js with the unique code, then verify it actually
 *      applied via the discount_codes array. THIS IS THE LOAD-BEARING GATE:
 *      Type A products are £0, so checkout-side rejection alone wouldn't
 *      block re-redemption — the burned-code signal at the cart layer is
 *      what blocks returning customers.
 *      • applied   → /checkout?discount=<CODE>
 *      • not applied → POST /cart/clear.js + render "claimed" state.
 */
(function () {
  var GEO_ENDPOINT = 'https://api.country.is/';
  var GEO_TIMEOUT_MS = 2500;
  // UK-only geo gate (ON for launch). Non-UK visitors see the "uk-only" state;
  // UK visitors get the quiz. Fails open (renders the quiz) if the geo API
  // errors or times out, so a third-party hiccup never blocks a legitimate UK
  // visitor; the UK-only checkout shipping profile is the hard backstop.
  var GEO_CHECK_ENABLED = true;
  var ALLOWED_COUNTRIES = ['GB'];
  var KLAVIYO_RENDER_POLL_MS = 200;
  var KLAVIYO_RENDER_MAX_TICKS = 30; // 6s
  var KLAVIYO_SUBMIT_TIMEOUT_MS = 15000;

  function init() {
    var root = document.querySelector('[data-sample-campaign]');
    if (!root) return;

    if (resolveOfferClosed(root)) return; // Server-side state, nothing to do.

    var designMode = root.getAttribute('data-design-mode') === 'true';
    var srcParam = readSrcParam();

    if (!designMode && !srcParam) {
      showState(root, 'unavailable');
      return;
    }

    if (designMode || !GEO_CHECK_ENABLED) {
      showQuiz(root);
    } else {
      runGeoCheck()
        .then(function (countryCode) {
          if (countryCode && ALLOWED_COUNTRIES.indexOf(countryCode) === -1) {
            showState(root, 'uk-only');
          } else {
            showQuiz(root);
          }
        })
        .catch(function () {
          showQuiz(root);
        });
    }

    wireQuizForm(root, srcParam || '');
  }

  function resolveOfferClosed(root) {
    return !!root.querySelector('[data-state="offer-closed"]');
  }

  function readSrcParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      var src = params.get('src');
      return src && src.trim() ? src.trim() : null;
    } catch (e) {
      return null;
    }
  }

  function showState(root, stateName) {
    root.querySelectorAll('[data-state]').forEach(function (node) {
      if (node.getAttribute('data-state') === stateName) {
        node.removeAttribute('hidden');
      } else {
        node.setAttribute('hidden', '');
      }
    });
  }

  function showQuiz(root) {
    showState(root, 'quiz');
  }

  function runGeoCheck() {
    return new Promise(function (resolve, reject) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('geo-timeout'));
      }, GEO_TIMEOUT_MS);

      var fetchOpts = controller ? { signal: controller.signal } : {};

      fetch(GEO_ENDPOINT, fetchOpts)
        .then(function (res) {
          clearTimeout(timer);
          if (!res.ok) throw new Error('geo-http-' + res.status);
          return res.json();
        })
        .then(function (data) {
          var country = data && (data.country || data.countryCode || data.country_code);
          resolve(country ? String(country).toUpperCase() : null);
        })
        .catch(function (err) {
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  function wireQuizForm(root, srcParam) {
    var form = root.querySelector('[data-sample-campaign-form]');
    if (!form) return;

    var submit = form.querySelector('[data-quiz-submit]');
    var email = form.querySelector('[data-quiz-email]');
    var questionGroups = ['quiz_q1', 'quiz_q2', 'quiz_q3'];

    function getAnswers() {
      var answers = {};
      for (var i = 0; i < questionGroups.length; i++) {
        var checked = form.querySelector('input[name="' + questionGroups[i] + '"]:checked');
        if (checked) answers[questionGroups[i]] = checked.value;
      }
      return answers;
    }

    function isComplete() {
      if (!email || !email.value || !/.+@.+\..+/.test(email.value)) return false;
      var answers = getAnswers();
      return Object.keys(answers).length === questionGroups.length;
    }

    function refresh() {
      if (!submit) return;
      if (isComplete()) {
        submit.removeAttribute('disabled');
        submit.setAttribute('aria-disabled', 'false');
      } else {
        submit.setAttribute('disabled', '');
        submit.setAttribute('aria-disabled', 'true');
      }
    }

    form.addEventListener('change', function (e) {
      refresh();
      maybeScrollToNext(form, e.target);
    });
    form.addEventListener('input', refresh);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!submit || submit.hasAttribute('disabled')) return;
      handleSubmit(root, form, submit, email, getAnswers, srcParam);
    });

    refresh();
  }

  function maybeScrollToNext(form, target) {
    if (!target || target.type !== 'radio') return;
    var currentFieldset = target.closest('[data-question]');
    if (!currentFieldset) return;

    var fieldsets = Array.prototype.slice.call(form.querySelectorAll('[data-question]'));
    var idx = fieldsets.indexOf(currentFieldset);
    var nextTarget = idx > -1 && idx < fieldsets.length - 1
      ? fieldsets[idx + 1]
      : form.querySelector('[data-quiz-email]');
    if (!nextTarget) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    setTimeout(function () {
      nextTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }

  function handleSubmit(root, form, submit, emailInput, getAnswers, srcParam) {
    var emailValue = (emailInput.value || '').trim();
    var answers = getAnswers();
    var formId = root.getAttribute('data-klaviyo-form-id');

    if (!formId) {
      console.error('[Sample Campaign] Klaviyo form ID missing on root element');
      showState(root, 'error');
      return;
    }
    if (!emailValue) return;

    setLoading(submit, true);

    submitKlaviyoForm(formId, emailValue)
      .then(function (code) {
        if (!code) throw new Error('no-code-extracted');
        identifyKlaviyo(root, emailValue, answers, srcParam);
        return processCart(root, code);
      })
      .then(function (result) {
        if (result.applied) {
          // Use a Shopify cart-permalink URL: this is the documented pattern
          // that reliably honors `checkout[email]` pre-fill (`/checkout?…`
          // does not in newer Shopify checkouts). The permalink replaces the
          // cart with the listed variants and forwards to checkout with the
          // discount + email applied. The /cart/add.js + /cart/update.js
          // calls upstream are still needed: cart/update.js is what surfaces
          // the `applicable` flag we use to gate returning customers.
          var permalinkPath = '/cart/' + result.variantIds.map(function (id) {
            return id + ':1';
          }).join(',');
          var checkoutUrl =
            permalinkPath +
            '?discount=' + encodeURIComponent(result.code) +
            '&checkout[email]=' + encodeURIComponent(emailValue);
          window.location.href = checkoutUrl;
        } else {
          trackBlocked(srcParam);
          showState(root, 'claimed');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        console.error('[Sample Campaign] Submit failed:', err && err.message ? err.message : err);
        setLoading(submit, false);
        showState(root, 'error');
      });
  }

  function submitKlaviyoForm(formId, email) {
    return new Promise(function (resolve, reject) {
      var container = document.querySelector('.klaviyo-form-' + formId);
      if (!container) return reject(new Error('klaviyo-container-missing'));

      var ticks = 0;
      var poll = setInterval(function () {
        ticks++;
        var klaviyoForm = container.querySelector('form');
        if (klaviyoForm) {
          clearInterval(poll);
          fillAndSubmit(container, klaviyoForm, email, resolve, reject);
        } else if (ticks >= KLAVIYO_RENDER_MAX_TICKS) {
          clearInterval(poll);
          reject(new Error('klaviyo-form-not-rendered'));
        }
      }, KLAVIYO_RENDER_POLL_MS);
    });
  }

  function fillAndSubmit(container, klaviyoForm, email, resolve, reject) {
    var emailField = klaviyoForm.querySelector('input[type="email"]');
    var submitBtn =
      klaviyoForm.querySelector('button[type="button"]') ||
      klaviyoForm.querySelector('button[type="submit"]') ||
      klaviyoForm.querySelector('button');

    if (!emailField || !submitBtn) {
      return reject(new Error('klaviyo-fields-missing'));
    }

    var done = false;
    var timeout = setTimeout(function () {
      if (done) return;
      done = true;
      observer.disconnect();
      reject(new Error('klaviyo-submit-timeout'));
    }, KLAVIYO_SUBMIT_TIMEOUT_MS);

    var observer = new MutationObserver(function () {
      var code = extractCouponCode(container);
      if (code && !done) {
        done = true;
        clearTimeout(timeout);
        observer.disconnect();
        resolve(code);
      }
    });
    observer.observe(container, { childList: true, subtree: true, characterData: true });

    var nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    ).set;
    nativeSetter.call(emailField, email);
    emailField.dispatchEvent(new Event('input', { bubbles: true }));
    emailField.dispatchEvent(new Event('change', { bubbles: true }));

    setTimeout(function () {
      submitBtn.click();
    }, 150);
  }

  // Accept either a `SAMPLE-…` prefixed code OR a plain alphanumeric 6–24
  // char code (Klaviyo's default short-code format, e.g. "452SNKJQ" / "XQMGWV6H").
  // The match is intentionally CASE-SENSITIVE (no /i): Klaviyo renders coupon
  // codes in UPPERCASE, whereas UI chrome such as the form's "Submit" button
  // label is mixed-case. Matching case-insensitively (the previous behaviour)
  // let the literal word "Submit" (6 letters) pass as a "code" — the observer
  // resolved on the button label before the real code rendered, so cart/update
  // was sent a bogus discount, Shopify returned applicable:false, and EVERY
  // customer saw the "already claimed" state. Anchored start-to-end so
  // multi-word strings ("THANK YOU!") or anything with spaces/punctuation are
  // rejected.
  var CODE_PREFIX_RE = /^(SAMPLE-)?[A-Z0-9]{6,24}$/;

  function extractCouponCode(container) {
    var readonly = container.querySelector('input[readonly]');
    if (readonly && readonly.value) {
      var v = readonly.value.trim();
      if (CODE_PREFIX_RE.test(v)) return v;
    }
    // NOTE: do NOT exclude button/interactive elements here — Klaviyo renders
    // the coupon code inside a click-to-copy control (DIV > BUTTON[role=button]),
    // so the code itself lives in a button. The case-sensitivity of
    // CODE_PREFIX_RE is what rejects the "Submit" button label without also
    // dropping the code.
    var els = container.querySelectorAll('div, span, p');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.children.length > 0) continue;
      var t = el.textContent.trim();
      if (t && CODE_PREFIX_RE.test(t)) return t;
    }
    return null;
  }

  // Attach the quiz answers + campaign source to the Klaviyo profile.
  //
  // This must NOT be a fire-and-forget _learnq push. On the happy path the page
  // redirects to checkout immediately after this runs, and Klaviyo's onsite
  // queue frequently has not flushed the identify to the network yet, so the
  // properties never attach. That produced the observed symptom: quiz answers
  // missing on a subset of profiles (intermittent, and more likely on
  // pre-existing contacts). Instead POST straight to Klaviyo's client-side
  // profiles API with keepalive:true, which the browser guarantees to deliver
  // even as the page navigates away. The endpoint upserts by email and merges
  // properties onto existing profiles, so new and returning contacts both work.
  // We keep the _learnq identify too, purely for onsite session association.
  function identifyKlaviyo(root, email, answers, srcParam) {
    var props = { campaign_src: srcParam };
    Object.keys(answers).forEach(function (k) {
      props[k] = answers[k];
    });

    try {
      window._learnq = window._learnq || [];
      var learnqProps = { $email: email };
      Object.keys(props).forEach(function (k) {
        learnqProps[k] = props[k];
      });
      window._learnq.push(['identify', learnqProps]);
    } catch (e) {
      // Non-blocking best-effort; the client API call below is load-bearing.
    }

    var companyId = root.getAttribute('data-klaviyo-public-key');
    if (!companyId) return;

    try {
      fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: { type: 'profile', attributes: { email: email, properties: props } }
        }),
        keepalive: true
      }).catch(function () {
        // Non-blocking: never let a Klaviyo hiccup stop the checkout redirect.
      });
    } catch (e) {
      // Non-blocking.
    }
  }

  function trackBlocked(srcParam) {
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Sample Re-attempt Blocked', { campaign_src: srcParam }]);
    } catch (e) {
      // Non-blocking.
    }
  }

  function processCart(root, code) {
    var variantIds = collectVariantIds(root);
    if (!variantIds.length) return Promise.reject(new Error('no-variant-ids'));

    var items = variantIds.map(function (id) {
      return { id: id, quantity: 1 };
    });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart-add-failed-' + r.status);
        return r.json();
      })
      .then(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: code })
        });
      })
      .then(function (r) {
        // Shopify returns 422 when the discount code is rejected (e.g. burned
        // single-use code, expired, or unknown). Treat as the "already claimed"
        // signal — at the Type A (£0 sample) layer, this is the only honest
        // way to distinguish a burned code from a fresh one, because both
        // produce `applicable: false` in the 200 response (see below).
        if (r.status === 422) {
          return r.json().then(function (body) {
            // Surface Shopify's reason text so a returning-customer 422 is
            // distinguishable from a config 422 in QA. No PII in this body.
            console.warn('[Sample Campaign] cart/update rejected (422):', body && (body.description || body.message || body));
            return { _rejected: true };
          }).catch(function () {
            return { _rejected: true };
          });
        }
        if (!r.ok) throw new Error('cart-update-failed-' + r.status);
        return r.json();
      })
      .then(function (cart) {
        // Type B architecture (sample priced > £0, 100%-off code): Shopify
        // returns `applicable: true` for a fresh code that actually discounts
        // the line, and `applicable: false` for a returning customer's burned
        // code (Shopify keeps it listed but silently refuses to apply because
        // of the per-customer limit). Under Type B, `applicable === true` IS
        // the honest signal. Burned/expired/unknown codes that Shopify drops
        // outright return 422 and are handled in the previous .then.
        // (Note: do NOT use this check under Type A / £0 products — there
        // `applicable` is always false and this gate would block all customers.)
        var accepted = false;
        if (cart && !cart._rejected && cart.discount_codes) {
          var upper = code.toUpperCase();
          for (var i = 0; i < cart.discount_codes.length; i++) {
            var entry = cart.discount_codes[i];
            if (entry && entry.code && entry.code.toUpperCase() === upper) {
              console.log('[Sample Campaign] discount_codes entry for', code, '→ applicable:', entry.applicable);
              if (entry.applicable === true) accepted = true;
              break;
            }
          }
        }
        if (accepted) {
          return { applied: true, code: code, variantIds: variantIds };
        }
        return clearCart().then(function () {
          return { applied: false, code: code };
        });
      });
  }

  function collectVariantIds(root) {
    var ids = [];
    root.querySelectorAll('[data-variant-id]').forEach(function (el) {
      var raw = el.getAttribute('data-variant-id');
      if (!raw) return;
      var id = parseInt(raw, 10);
      if (!isNaN(id) && id > 0) ids.push(id);
    });
    return ids;
  }

  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    }).catch(function () {
      // Non-blocking — claimed UI still renders.
    });
  }

  function setLoading(submit, on) {
    if (!submit) return;
    if (on) {
      submit.setAttribute('disabled', '');
      submit.setAttribute('aria-disabled', 'true');
      submit.classList.add('btn--loading');
      if (!submit.querySelector('.loading__spinner')) {
        submit.insertAdjacentHTML(
          'beforeend',
          '<div class="loading__spinner">' +
            '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
            '<circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>' +
            '</svg></div>'
        );
      }
    } else {
      submit.classList.remove('btn--loading');
      var spinner = submit.querySelector('.loading__spinner');
      if (spinner) spinner.remove();
      submit.removeAttribute('disabled');
      submit.setAttribute('aria-disabled', 'false');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
